import { useCallback } from 'react';
import { useCannedResponsesMenu } from './useCannedResponsesMenu';
import { useChatInput } from './useChatInput';
import { useChatTyping } from './useChatTyping';

type TKeyboardPressTextArea = React.KeyboardEvent<HTMLTextAreaElement>;
type TChangeTextArea = React.ChangeEvent<HTMLTextAreaElement>;

export const useChatInputEvents = () => {
  const { chatMode, message, setMessage, sendMessage } = useChatInput();
  const { setTyping } = useChatTyping();
  const {
    increaseCannedResponseIndex,
    decreaseCannedResponseIndex,
    setCurrentResponseBasedOnIndex,
    isCannedResponsesMenuOpen,
    toggleCannedResponsesMenuOpen,
  } = useCannedResponsesMenu();

  const handleInputChange = useCallback(
    (event: TChangeTextArea) => {
      const {
        target: { value },
      } = event;

      setMessage(value);
      setTyping();
    },
    [setMessage, setTyping],
  );

  const handleTextAreaKeyDown = useCallback(
    (event: TKeyboardPressTextArea) => {
      // Keydown events
      const handleCannedResponseUpEvent = (event: TKeyboardPressTextArea) => {
        const { code } = event;

        if (code === 'ArrowUp') {
          decreaseCannedResponseIndex();
          event.preventDefault();
        }
      };

      const handleCannedResponseDownEvent = (event: TKeyboardPressTextArea) => {
        const { code } = event;

        if (code === 'ArrowDown') {
          increaseCannedResponseIndex();
          event.preventDefault();
        }
      };

      const handleCannedResponseEnterEvent = (event: TKeyboardPressTextArea) => {
        const { code } = event;

        if (code === 'Enter' || code === 'NumpadEnter') {
          setCurrentResponseBasedOnIndex();
          event.preventDefault();
        }
      };

      const handleCannedResponseBackspaceEvent = (event: TKeyboardPressTextArea) => {
        const { code } = event;

        if (code === 'Backspace') {
          toggleCannedResponsesMenuOpen();
          event.preventDefault();
        }
      };

      const handleCannedResponseSlashEvent = (event: TKeyboardPressTextArea) => {
        const { code } = event;

        if (message === '' && chatMode === 'reply' && code === 'Slash') {
          toggleCannedResponsesMenuOpen();
          event.preventDefault();
        }
      };

      const { code } = event;

      // Switch in and out of canned response mode via slash
      handleCannedResponseSlashEvent(event);

      if (isCannedResponsesMenuOpen) {
        handleCannedResponseUpEvent(event);
        handleCannedResponseDownEvent(event);
        handleCannedResponseEnterEvent(event);
        handleCannedResponseBackspaceEvent(event);
      } else {
        // On enter send the message
        if (code === 'Enter' || code === 'NumpadEnter') {
          sendMessage();
          setMessage('');
          event.preventDefault();
        }
      }
    },
    [
      isCannedResponsesMenuOpen,
      chatMode,
      decreaseCannedResponseIndex,
      increaseCannedResponseIndex,
      message,
      sendMessage,
      setCurrentResponseBasedOnIndex,
      setMessage,
      toggleCannedResponsesMenuOpen,
    ],
  );

  return {
    handleInputChange: handleInputChange,
    handleInputKeydown: handleTextAreaKeyDown,
  };
};
